/**
 * HrDashboard — pulpit obszaru Kadry/HR (Etap 1).
 *
 * Wzorowany na pulpicie HR z ITE Workflow365: czysta siatka kafelków KPI (bez
 * wykresów), pogrupowana nagłówkami. Dane z `GET /api/hr/metrics` (stan
 * zatrudnienia + fluktuacja + struktura + oczekujące wnioski) — patrz
 * apps/api/lib/hr/metrics.ts. Alerty terminów BHP/certyfikatów świadomie poza
 * Etapem 1 (model danych w modułach Kreatora — Etap 2).
 *
 * Uwaga i18n: teksty PL inline na czas Etapu 1 (jak components/login/areas.ts);
 * ekstrakcja do `hr.*` przed mergem — praca eksperymentalna na branchu `dobro`.
 */

import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Building2,
  CalendarOff,
  FileText,
  Plane,
  RefreshCw,
  TrendingUp,
  UserMinus,
  UserPlus,
  Users,
  UserX,
} from 'lucide-react';

import { Button } from '@/components/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { PageHeaderBar } from '@/components/ui/PageHeaderBar';
import { StatCard, type StatCardColor } from '@/components/ui/StatCard';
import { api } from '@/lib/api';
import { HR_METRICS_QUERY_KEY } from '@/lib/hr/query-keys';

interface HrMetrics {
  headcount: number;
  hires12m: number;
  exits12m: number;
  turnoverPct: number;
  departments: number;
  withoutDepartment: number;
  /**
   * `urlopy: null` = nie wiadomo, który typ dokumentu jest wnioskiem o nieobecność (klient wybrał
   * „brak", brak typu domyślnego ITE, wybrany typ usunięty/wycofany albo katalog niedostępny) —
   * kafelek pokazuje „Brak danych". `delegacje: null` = katalog ITE niedostępny (nie wiadomo,
   * który typ jest delegacją) — też „Brak danych". `total` jest `null`, gdy `urlopy` albo
   * `delegacje` jest `null` (sumy nie da się policzyć).
   */
  pending: {
    urlopy: number | null;
    delegacje: number | null;
    zmianyDanych: number;
    total: number | null;
  };
}

/** Karta alertowa: amber gdy >0, szara gdy 0. */
function alertColor(n: number): StatCardColor {
  return n > 0 ? 'amber' : 'gray';
}

function Group({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section>
      <h3 className="mb-3 text-sm font-semibold text-text-secondary">{title}</h3>
      {children}
    </section>
  );
}

/** Wartość kafelka bez danych: mniejszy, wyszarzony tekst zamiast liczby (StatCard sam nie koloruje wartości). */
function NoDataValue({ testId }: { testId: string }) {
  const { t } = useTranslation();
  return (
    <span className="text-base font-medium text-text-muted" data-testid={testId}>
      {t('hr.dashboard.noData')}
    </span>
  );
}

function SkeletonCard() {
  return (
    <div className="h-[92px] animate-pulse rounded-xl border border-border-secondary bg-bg-secondary" />
  );
}

export function HrDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const query = useQuery({
    queryKey: HR_METRICS_QUERY_KEY,
    queryFn: async () => {
      const res = await api.get<{ metrics: HrMetrics }>('/hr/metrics');
      return res.data.metrics;
    },
    staleTime: 60_000,
  });

  const m = query.data;
  const retentionPct =
    m && m.headcount > 0 ? Math.max(0, Math.round((100 - m.turnoverPct) * 10) / 10) : null;

  const goApprovals = () => navigate('/processes/decisions/pending');

  return (
    <div className="space-y-6 p-6">
      <PageHeaderBar
        title={t('hr.dashboard.title')}
        description={t('hr.dashboard.description')}
        actions={
          <Button
            variant="secondary"
            size="sm"
            leftIcon={
              <RefreshCw className={query.isFetching ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
            }
            onClick={() => query.refetch()}
            disabled={query.isFetching}
          >
            {t('hr.dashboard.refresh')}
          </Button>
        }
      />

      {query.isError ? (
        <EmptyState
          icon={Users}
          title={t('hr.dashboard.errorTitle')}
          description={t('hr.dashboard.errorDescription')}
          action={
            <Button variant="secondary" size="sm" onClick={() => query.refetch()}>
              {t('hr.dashboard.retry')}
            </Button>
          }
        />
      ) : query.isLoading ? (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        </div>
      ) : m ? (
        <>
          <Group title={t('hr.dashboard.groupEmployment')}>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              <StatCard
                label={t('hr.dashboard.headcount')}
                value={m.headcount}
                icon={Users}
                iconColor="indigo"
              />
              <StatCard
                label={t('hr.dashboard.hires12m')}
                value={m.hires12m}
                icon={UserPlus}
                iconColor="green"
              />
              <StatCard
                label={t('hr.dashboard.exits12m')}
                value={m.exits12m}
                icon={UserMinus}
                iconColor="amber"
              />
              <StatCard
                label={t('hr.dashboard.turnover')}
                value={`${m.turnoverPct}%`}
                icon={TrendingUp}
                iconColor={m.turnoverPct > 0 ? 'red' : 'gray'}
                hint={
                  retentionPct !== null
                    ? t('hr.dashboard.retentionHint', { pct: retentionPct })
                    : undefined
                }
              />
            </div>
          </Group>

          <Group title={t('hr.dashboard.groupStructure')}>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              <StatCard
                label={t('hr.dashboard.departments')}
                value={m.departments}
                icon={Building2}
                iconColor="blue"
              />
              <StatCard
                label={t('hr.dashboard.withoutDepartment')}
                value={m.withoutDepartment}
                icon={UserX}
                iconColor={alertColor(m.withoutDepartment)}
              />
            </div>
          </Group>

          <Group title={t('hr.dashboard.groupPending')}>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
              <StatCard
                label={t('hr.dashboard.pendingLeaves')}
                value={
                  m.pending.urlopy ?? <NoDataValue testId="hr-dashboard-pending-leaves-no-data" />
                }
                icon={CalendarOff}
                iconColor={alertColor(m.pending.urlopy ?? 0)}
                onClick={goApprovals}
                data-testid="hr-dashboard-pending-leaves"
              />
              <StatCard
                label={t('hr.dashboard.pendingDelegations')}
                value={
                  m.pending.delegacje ?? (
                    <NoDataValue testId="hr-dashboard-pending-delegations-no-data" />
                  )
                }
                icon={Plane}
                iconColor={alertColor(m.pending.delegacje ?? 0)}
                onClick={goApprovals}
                data-testid="hr-dashboard-pending-delegations"
              />
              <StatCard
                label={t('hr.dashboard.pendingDataChanges')}
                value={m.pending.zmianyDanych}
                icon={FileText}
                iconColor={alertColor(m.pending.zmianyDanych)}
                onClick={goApprovals}
              />
            </div>
          </Group>
        </>
      ) : (
        <EmptyState
          icon={Users}
          title={t('hr.dashboard.emptyTitle')}
          description={t('hr.dashboard.emptyDescription')}
        />
      )}
    </div>
  );
}

export default HrDashboard;
